import { useRef, useState, useEffect } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import Confetti from "../components/Confetti";
import CoinRain from "../components/CoinRain";
import { formatCoins } from "../lib/format";
import { playScratch, playWin, playLose } from "../lib/sound";

const COST = 50;

export default function ScratchCard() {
  const { t } = useLang();
  const { setCoins } = useAuth();
  const canvasRef = useRef(null);
  const [round, setRound] = useState(null);
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [confetti, setConfetti] = useState(0);
  const [rain, setRain] = useState(0);
  const drawing = useRef(false);

  const buy = async () => {
    if (busy) return;
    setBusy(true);
    setRevealed(false);
    try {
      const { data } = await api.post("/games/scratch");
      setCoins(data.coins);
      setRound(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!round) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    grad.addColorStop(0, "#C9A227");
    grad.addColorStop(0.5, "#F6D860");
    grad.addColorStop(1, "#B8860B");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "rgba(0,0,0,0.35)";
    ctx.font = "bold 20px Outfit";
    ctx.textAlign = "center";
    ctx.fillText("SCRATCH HERE", canvas.width / 2, canvas.height / 2 - 6);
    ctx.fillText("यहाँ खुरचें", canvas.width / 2, canvas.height / 2 + 22);
    ctx.globalCompositeOperation = "destination-out";
  }, [round]);

  const scratchAt = (e) => {
    if (!drawing.current || revealed) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    const ctx = canvas.getContext("2d");
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    ctx.beginPath();
    ctx.arc(cx * sx, cy * sy, 22, 0, Math.PI * 2);
    ctx.fill();
    if (Math.random() > 0.7) playScratch();
    checkCleared();
  };

  const checkCleared = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let clear = 0;
    for (let i = 3; i < data.length; i += 40) if (data[i] === 0) clear++;
    if (clear / (data.length / 40) > 0.55) finish();
  };

  const finish = () => {
    if (revealed) return;
    setRevealed(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (round.win > 0) {
      setConfetti(Date.now());
      setRain(Date.now());
      playWin(round.win);
      toast.success(`${t("youWon")} +${formatCoins(round.win)} ${t("coins")}!`);
    } else {
      playLose();
      toast(`${t("youLost")} (-${COST})`);
    }
  };

  return (
    <div className="space-y-6">
      <Confetti fire={confetti} />
      <CoinRain fire={rain} />
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold gold-text">{t("scratchGame")}</h1>
        <p className="text-sm text-slate-400 mt-1">{t("howToPlay")}: ₹{COST} → {t("scratchNow")} · 3 same = win</p>
      </div>

      <div className="mw-card p-6 max-w-md mx-auto flex flex-col items-center gap-5">
        {!round ? (
          <div className="text-center py-10">
            <Sparkles size={56} className="mx-auto text-amber-400 animate-pulse-glow rounded-full" />
            <p className="mt-4 text-slate-400">{t("scratchGame")}</p>
          </div>
        ) : (
          <div className="relative" style={{ width: 300, height: 300 }}>
            <div className="absolute inset-0 grid grid-cols-3 gap-2 p-2 rounded-xl bg-black/40 border border-amber-500/30">
              {round.grid.map((v, i) => (
                <div key={i} className="grid place-items-center rounded-lg bg-[#1b1e2c] font-mono-num font-bold text-amber-200"
                     style={{ fontSize: v >= 1000 ? 14 : 18, color: v === round.prize && round.prize > 0 ? "#FFD700" : "#94A3B8" }}
                     data-testid={`scratch-cell-${i}`}>
                  {v === 0 ? "✕" : v}
                </div>
              ))}
            </div>
            <canvas
              ref={canvasRef}
              width={300}
              height={300}
              className="absolute inset-0 rounded-xl cursor-grab touch-none"
              style={{ width: 300, height: 300, display: revealed ? "none" : "block" }}
              onMouseDown={() => (drawing.current = true)}
              onMouseUp={() => (drawing.current = false)}
              onMouseLeave={() => (drawing.current = false)}
              onMouseMove={scratchAt}
              onTouchStart={() => (drawing.current = true)}
              onTouchEnd={() => (drawing.current = false)}
              onTouchMove={scratchAt}
              data-testid="scratch-canvas"
            />
          </div>
        )}

        {round && !revealed && (
          <button onClick={finish} data-testid="scratch-reveal-btn" className="text-sm text-cyan-300 hover:underline">
            {t("result")} →
          </button>
        )}
        {revealed && (
          <p className="font-display text-2xl font-extrabold" style={{ color: round.win > 0 ? "#00E676" : "#FF3366" }} data-testid="scratch-result">
            {round.win > 0 ? `+${formatCoins(round.win)}` : `-${COST}`}
          </p>
        )}

        <button onClick={buy} disabled={busy} data-testid="scratch-buy-btn" className="w-full btn-gold rounded-xl py-3 text-lg flex items-center justify-center gap-2">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
          {t("scratchNow")} ({COST})
        </button>
      </div>
    </div>
  );
}
